import { inject, Injectable } from '@angular/core';
import { AuthPayload } from './models/auth.payload';
import { environment } from '../../../../environments/environment';
import { TokenDto } from './models/token.dto';
import { FirstAccessDto } from './models/first-access.dto';
import { FirstAccessPayload } from './models/first-access.payload';
import { LocalStorage } from '../local-storage/local-storage.service';
import { MeService } from '../me/me.service';
import { Router } from '@angular/router';
import { Api } from '../api/api';
import { isValidMockCredentials } from '../../mocks/interfaces/me.mock';
import { ensureBearerToken } from '../../utils/bearer-token.util';
import { browserNavigation } from '../../utils/browser-navigation.util';
import { getTokenExpiration, isImpersonatedToken } from '../../utils/jwt-claims.util';
import { ToastService } from '../../components/toast/service/toast.service';
import { buildSessionHandoffUrl } from '../../utils/session-handoff.util';

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  constructor(
    private readonly router: Router,
    private readonly meService: MeService,
    private readonly localStorage: LocalStorage,
  ) { }

  private readonly api: Api = new Api();

  private stopRequest: Promise<void> | null = null;

  private refreshRequest: Promise<boolean> | null = null;

  private readonly toastService: ToastService = inject(ToastService);

  //#region Real API / public entrypoints

  public async login(payload: AuthPayload): Promise<void> {
    if (environment.useFakeBackend) {
      return this.loginMocked(payload);
    }

    const response = await this.api.postAsync<TokenDto>(environment.routes.auth.login, payload);

    if (response?.success) {
      this.saveToken(response.success);
      await this.meService.storageMe();
    }
  }

  public async refreshToken(): Promise<boolean> {
    // Requisições simultâneas com o token expirado compartilham o mesmo refresh
    this.refreshRequest ??= this.requestRefreshToken()
      .catch(() => false)
      .finally(() => {
        this.refreshRequest = null;
      });

    return this.refreshRequest;
  }


  public async forgotPassword(email: string): Promise<boolean> {
    if (environment.useFakeBackend) {
      return this.forgotPasswordMocked(email);
    }

    const response = await this.api.postAsync<{ isEmailSent?: boolean; ok?: boolean }>(
      environment.routes.auth.forgotPassword,
      { email },
    );

    if (response?.error) {
      throw response.error;
    }

    return !!response?.success;
  }

  public async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<string> {
    const response = await this.api.postAsync<{ ok: boolean; message: string }>(
      environment.routes.auth.changePassword,
      { currentPassword, newPassword, confirmPassword },
    );

    if (response?.error) {
      throw response.error;
    }

    return response?.success?.message ?? 'Senha alterada com sucesso.';
  }

  public async resetPassword(password: string, confirmPassword: string, resetPasswordCode: string): Promise<boolean> {
    if (environment.useFakeBackend) {
      return this.resetPasswordMocked(password, confirmPassword, resetPasswordCode);
    }

    const response = await this.api.postAsync<{ ok: boolean }>(
      environment.routes.auth.resetPassword,
      {
        resetPasswordCode,
        newPassword: password,
        confirmPassword,
      },
    );

    if (response?.error) {
      throw response.error;
    }

    return !!response?.success?.ok || !!response?.success;
  }

  public async getFirstAccess(token: string, email: string): Promise<FirstAccessDto> {
    const response = await this.api.getAsync<FirstAccessDto>(environment.routes.auth.firstAccess, {
      params: { token, email },
    });

    if (response?.error) {
      throw response.error;
    }

    if (!response?.success) {
      throw new Error('Não foi possível validar o link de primeiro acesso.');
    }

    return response.success;
  }

  public async defineFirstAccessPassword(payload: FirstAccessPayload): Promise<string> {
    const response = await this.api.postAsync<{ ok: boolean; message: string }>(
      environment.routes.auth.firstAccess,
      payload,
    );

    if (response?.error) {
      throw response.error;
    }

    return response?.success?.message ?? 'Senha definida com sucesso.';
  }

  public async logout(): Promise<void> {
    this.localStorage.clear();
    await this.router.navigateByUrl('/login');
  }

  public isLoggedIn(): boolean {
    if (!this.getToken())
      return false;

    // Sessão com refresh token vencido não pode ser renovada: limpa para o login não reaproveitar o token
    if (this.isSessionExpired()) {
      this.clearSession();
      return false;
    }

    return true;
  }

  public isSessionExpired(now: number = Date.now()): boolean {
    const token = this.getToken();

    if (!token)
      return false;

    if (!token.refreshToken)
      return true;

    const expiresAt = getTokenExpiration(token.refreshToken) ?? Date.parse(token.refreshExpiresAt);

    return !Number.isNaN(expiresAt) && expiresAt <= now;
  }

  public async expireSession(): Promise<void> {
    if (!this.getToken())
      return;

    this.clearSession();
    this.toastService.warn('Sua sessão expirou. Faça login novamente.', 'Sessão encerrada');
    await this.router.navigateByUrl(environment.configs.unauthenticatedRoute, { replaceUrl: true });
  }

  public clearSession(): void {
    this.localStorage.clear();
  }

  public getToken(): TokenDto | null {
    return this.localStorage.getItem<TokenDto>(environment.keys.token);
  }

  public isImpersonating(): boolean {
    return isImpersonatedToken(this.getToken()?.token);
  }

  public async acceptImpersonation(token: TokenDto): Promise<void> {
    this.saveToken(token);
    await this.meService.storageMe();
  }

  public async stopImpersonation(): Promise<void> {
    if (this.stopRequest) {
      return this.stopRequest;
    }

    this.stopRequest = this.executeStopImpersonation().finally(() => {
      this.stopRequest = null;
    });

    return this.stopRequest;
  }

  public isAccessTokenExpired(referenceDate: Date = new Date()): boolean {
    const token = this.getToken();

    if (!token?.expiresAt) {
      return false;
    }

    const expiresAt = new Date(token.expiresAt);

    if (Number.isNaN(expiresAt.getTime())) {
      return false;
    }

    return expiresAt.getTime() <= referenceDate.getTime();
  }

  //#endregion

  //#region Fake Backend

  public async loginMocked(payload: AuthPayload): Promise<void> {
    if (!isValidMockCredentials(payload.username, payload.password)) {
      throw Object.assign(new Error('Credenciais inválidas'), {
        error: { message: 'Credenciais inválidas' },
      });
    }

    const now = Date.now();
    const token: TokenDto = {
      token: `fake-jwt-${now}`,
      refreshToken: `fake-refresh-${now}`,
      expiresAt: new Date(now + 60 * 60 * 1000).toISOString(),
      refreshExpiresAt: new Date(now + 24 * 60 * 60 * 1000).toISOString(),
    };

    this.saveToken(token);
    await this.meService.storageMeMocked(payload.username);
  }

  public async refreshTokenMocked(): Promise<boolean> {
    const current = this.getToken();

    if (!current) {
      return false;
    }

    const now = Date.now();
    this.saveToken({
      token: `fake-jwt-refreshed-${now}`,
      refreshToken: current.refreshToken,
      expiresAt: new Date(now + 60 * 60 * 1000).toISOString(),
      refreshExpiresAt: current.refreshExpiresAt,
    });

    return true;
  }

  public async forgotPasswordMocked(email: string): Promise<boolean> {
    return !!email?.trim();
  }

  public async resetPasswordMocked(password: string, confirmPassword: string, resetPasswordCode = 'MOCKCODE'): Promise<boolean> {
    if (!resetPasswordCode?.trim()) {
      throw Object.assign(new Error('O código de redefinição é inválido'), {
        error: { message: 'O código de redefinição é inválido' },
      });
    }

    if (!password?.trim() || password !== confirmPassword) {
      throw Object.assign(new Error('As senhas não coincidem'), {
        error: { message: 'As senhas não coincidem' },
      });
    }

    return true;
  }

  //#endregion

  private async requestRefreshToken(): Promise<boolean> {
    if (environment.useFakeBackend) {
      return this.refreshTokenMocked();
    }

    const current = this.getToken();

    if (!current?.refreshToken) {
      return false;
    }

    const api = new Api();

    const token = await api.postAsync<TokenDto>(
      environment.apiUrl + environment.routes.auth.refresh,
      {},
      {
        cancelInterceptors: true,
        headers: {
          Authorization: ensureBearerToken(current.refreshToken),
        },
      },
    );

    if (token?.success)
      this.saveToken(token.success);

    return !!token?.success;
  }

  private async executeStopImpersonation(): Promise<void> {
    const response = await this.api.postAsync<TokenDto>(environment.routes.auth.impersonateStop, {});
    const restored = response?.success;

    if (!restored?.token || !restored.refreshToken) {
      throw Object.assign(new Error('Não foi possível encerrar a impersonação.'), {
        error: { message: 'Não foi possível encerrar a impersonação.' },
      });
    }

    this.localStorage.remove(environment.keys.token);
    this.localStorage.remove(environment.keys.user);
    browserNavigation.assign(buildSessionHandoffUrl(
      `${environment.apps.master.replace(/\/$/, '')}/session/restore`,
      restored,
    ));
  }

  private saveToken(token: TokenDto): void {
    this.localStorage.setItem(environment.keys.token, {
      ...token,
      token: ensureBearerToken(token.token),
      refreshToken: ensureBearerToken(token.refreshToken),
    });
  }

}
